import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import MarkedProgress from 'UIKit/Progress/MarkedProgress'
import { StyledMarkedProgressPoint } from 'UIKit/Progress/MarkedProgress/styled'
import Text from 'UIKit/Text'
import HunnyTooltip from 'UIKit/Tooltip/Tooltip'
import { UsedBonusStatus } from 'config/constants/bonus'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import styled, { css } from 'styled-components'
import theme from 'theme'
import { freeSpinBonusFiatReward } from 'utils/bonus'
import { getFullDisplayBalance, getFullDisplayFixedDisplayBalance } from 'utils/formatBalance'
import { ValidatedBonusStatusEnums, useValidateBonus } from 'views/BonusCenter/hooks'

const DONE_PERCENT_NUMBER = 100

const WagerBonusMilestoneProgressBar: React.FC<
  { bonus: WageringConditionBonus; isShowMonochrome?: boolean } & BoxProps
> = ({ bonus, isShowMonochrome, ...props }) => {
  const verifyResult = useValidateBonus(bonus)
  const isBonusUsed = [...UsedBonusStatus].concat([ValidatedBonusStatusEnums.Cancelled]).includes(verifyResult)
  const isExistShowMonochrome = typeof isShowMonochrome === 'boolean'
  const isDone = bonus.progress === DONE_PERCENT_NUMBER
  const freeSpinCurrency = freeSpinBonusFiatReward(bonus)
  const isPlayToClaimBonus = !bonus.isCreaditReward

  const renderImageStep = (isClaimed: boolean) => {
    const basePath = isClaimed ? '/images/bonus/milestone-success-icon.png' : '/images/bonus/milestone-point.png'
    const usedPath = isClaimed
      ? '/images/bonus/used-milestone-success-icon.png'
      : '/images/bonus/used-milestone-point.png'

    if (isShowMonochrome) return usedPath

    if (isExistShowMonochrome) {
      return isDone ? basePath : usedPath
    }

    return isBonusUsed ? usedPath : basePath
  }

  return (
    <Box {...props}>
      <MarkedProgress
        markedList={bonus.milestones.map((item) => ({
          value: item.wagerPercentage,
          ...item,
        }))}
        height="6px !important"
        // eslint-disable-next-line react/no-unstable-nested-components
        markComponent={({ item, isActive }) => {
          const isClaimed = isActive && item.isClaimed

          return (
            <Box
              data-tooltip-id={`wager-percentage-in-usd-${bonus.id}`}
              data-tooltip-content={`${getFullDisplayFixedDisplayBalance(item.wagerAmount, 0, {
                max: 2,
              })} ${!bonus.isCreaditReward ? 'USD' : bonus?.bonusAmount?.token?.name}`}
              position="relative"
              right="4px"
            >
              {isActive || isClaimed ? (
                <StyledActiveMilestone $isClaimed={isClaimed} $isUsed={isBonusUsed}>
                  <Box style={{ transform: isBonusUsed ? 'scale(0.68)' : 'scale(0.8)' }}>
                    <Image src={renderImageStep(isClaimed)} width={12} height={12} />
                  </Box>
                </StyledActiveMilestone>
              ) : (
                <StyledMarkedProgressPoint />
              )}
            </Box>
          )
        }}
        value={bonus.progress}
        barStyles={{
          background: isExistShowMonochrome
            ? isDone && !isShowMonochrome
              ? `${theme.colors.success} !important`
              : '#787d8a !important'
            : isBonusUsed
            ? '#787d8a !important'
            : 'linear-gradient(252deg, rgb(0 207 8 / 70%) -3.62%, rgb(0 254 117 / 81%) 124.68%) !important',
          borderTopRightRadius: '12px',
          borderBottomRightRadius: '12px',
          height: 'calc(100% - 2px) !important',
          top: '0.85px !important',
          left: '1px !important',
        }}
        my="13px"
        background={'#4d525e !important'}
      />
      <HunnyTooltip id={`wager-percentage-in-usd-${bonus.id}`} place="top" />
      <Flex justifyContent="space-between" mt="4px">
        <Flex>
          <Text bold fontSize="12px" letterSpacing="-0.02em" lineHeight="15px">
            {getFullDisplayBalance(bonus.currentWagerBigAmount, 0, 5)}
          </Text>
          <Text bold fontSize="12px" letterSpacing="-0.02em" mx="4px" lineHeight="15px">
            /
          </Text>
          <Text bold fontSize="12px" letterSpacing="-0.02em" lineHeight="15px">
            {getFullDisplayBalance(bonus.targetWagerBigAmount, 0, 5)}
          </Text>
          <Text bold fontSize="12px" color="textAlt1" lineHeight="15px" ml="4px">
            {isPlayToClaimBonus ? 'USD' : bonus.bonusAmount?.token.name || (freeSpinCurrency && freeSpinCurrency.code)}
          </Text>
        </Flex>
        <Text bold fontSize="12px" letterSpacing="-0.02em" lineHeight="15px">
          {bonus.progress}%
        </Text>
      </Flex>
    </Box>
  )
}

const StyledActiveMilestone = styled(StyledMarkedProgressPoint)<{
  $isClaimed: boolean
  $isUsed: boolean
}>`
  background: ${({ $isUsed }) => ($isUsed ? '#9f9e9e85' : '#ffffff6e')};
  transform: scale(1.6);
  transition: ${({ theme: { transitions } }) => transitions.fast};
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    border-radius: 50% !important;
  }

  ${({ $isClaimed, $isUsed }) =>
    !$isClaimed &&
    !$isUsed &&
    css`
      animation: pulse-ring 1.5s linear forwards infinite;

      @keyframes pulse-ring {
        0% {
          transform: scale(1.7);
        }
        50% {
          opacity: 1;
          transform: scale(1.9);
        }
        100% {
          opacity: 1;
          transform: scale(1.7);
        }
      }
    `}

  &:hover {
    transform: scale(1.9);
  }

  &::after,
  &::before {
    display: none;
  }
`

export default WagerBonusMilestoneProgressBar
